// packages/pulse-sdk/src/client.ts
// ─────────────────────────────────────────────────────────────────────────────
// PULSE fetch client. The SDK never owns sign-in (that's the host app, or the
// identity broker in Phase 4) — it accepts a `getToken()` callback that returns
// the current short-lived S2S token. The token is requested per call so a
// host that rotates it (5-min TTL) always sends a fresh one.
//
// Security posture mirrors apps/web: the token is held by the host, passed
// through `Authorization: Bearer`, and never persisted by the SDK.
// ─────────────────────────────────────────────────────────────────────────────

export class PulseApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown
  ) {
    super(message);
    this.name = "PulseApiError";
  }
}

export type GetToken = () => string | null | Promise<string | null>;

export interface PulseClientConfig {
  /** Base URL for the PULSE API. "" => same-origin. No trailing slash. */
  apiBaseUrl: string;
  /** Returns the current S2S token (or null when signed out). */
  getToken: GetToken;
  /** Optional fetch override (tests / SSR). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export class PulseClient {
  private readonly base: string;
  private readonly getToken: GetToken;
  private readonly fetchImpl: typeof fetch;

  constructor(config: PulseClientConfig) {
    this.base = config.apiBaseUrl.replace(/\/$/, "");
    this.getToken = config.getToken;
    // Default to the global fetch, bound to its realm. An unbound reference
    // throws "Illegal invocation" in browsers when called as a method.
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; auth?: boolean } = {}
  ): Promise<T> {
    const headers: Record<string, string> = {};
    const auth = opts.auth ?? true;
    if (auth) {
      const token = await this.getToken();
      if (!token) throw new PulseApiError(401, "not authenticated");
      headers["authorization"] = `Bearer ${token}`;
    }
    if (opts.body !== undefined) headers["content-type"] = "application/json";

    const res = await this.fetchImpl(`${this.base}${path}`, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });

    const text = await res.text();
    const parsed = text ? safeJson(text) : undefined;
    if (!res.ok) {
      const msg =
        (parsed && typeof parsed === "object" && "error" in parsed
          ? String((parsed as { error: unknown }).error)
          : res.statusText) || "request failed";
      throw new PulseApiError(res.status, msg, parsed);
    }
    return parsed as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("POST", path, { body });
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Build a query string from defined params only. */
export function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}
